/**
 * The sign-in gate — client review R1 (2026-08-22), rebuilt to the client's mock by **C5**.
 *
 * The geoportal's **first** page: a session goes gate → §F4 splash → app. It is an overlay
 * on the same pattern as the splash (a sibling of `<AppRoutes/>` in `App.tsx`, never a
 * route) for the same reason — the app behind it must stay mounted, so that dismissing the
 * gate is a paint and not a boot — and it suppresses itself for the session under its own
 * `sessionStorage` key (`GATE_SEEN_KEY`, `lib/state.ts`).
 *
 * ## C5: a page, not a card
 *
 * R1 shipped a 940 px centred card. The client's mock is a full-bleed two-panel page, and the
 * difference is not decoration: the left panel is where the programme introduces itself — the
 * donor wordmark, what the portal holds, which four countries, and the coalition behind it —
 * and none of that fits in the margin of a login box. So the left half is a rounded photo
 * panel (IFDC's, over a dark overlay; `gateAssets.ts` falls back to a soil-green gradient if
 * the file is ever absent) and the right half is the white form: logo, *Welcome*, email,
 * password with a reveal toggle, the two inert conveniences (Remember me, Forgot password?),
 * and the button.
 *
 * ## 2026-09-02: the attribution is one band, the way the brand guide draws it
 *
 * The panel used to carry three blocks top to bottom — a white card holding the Netherlands
 * wordmark at the top, the copy in the middle, a *Coalition Partners* card at the bottom —
 * which put the two attributions at opposite ends of the panel with the programme's own pitch
 * wedged between them. IFDC's brand guide arranges them as one band: *Funded by* with the
 * donor mark on the left, *Coalition Partners* with the partner grid on the right, one dashed
 * rule under both headings. That is now the shape here, as a single card at the foot of the
 * panel, and the copy takes the whole space above it instead of a slot between two cards.
 *
 * The guide splits the partner marks 3 + 5 across its two rows. That is a reading of a band
 * as wide as a page; this one is half a screen, and five marks across ~350 px is 66 px each.
 * The order the guide names is kept exactly (`gateAssets.ts` owns it) and the wrapping is
 * 4 + 4, which is the same eight logos at a size a reader can identify.
 *
 * ## 2026-09-08, client specification S1: the logo block, and the client's own copy
 *
 * `docs/client-specs/2026-09-08-entry-s1.md` is the contract for this page. Four changes, all
 * inside it, and nothing outside it moved.
 *
 * **The marks are one even grid.** Priority 1 is the client's visible complaint, and the fix
 * has three parts that only work together: the source files were trimmed of their dead border
 * (`scripts/trim-logos.py` — CSS cannot undo padding baked into an image), every mark now sits
 * in a cell of exactly one size with `object-fit:contain`, and each declares how much of that
 * cell's height its shape may take so that a 5:1 wordmark and a 1:1 square mark carry the same
 * weight. The donor mark gets the same treatment in its own slot, one size up. See the band's
 * own comment below.
 *
 * The band's *arrangement* is untouched by all of that, and after two client corrections that is
 * emphatically deliberate: the two attributions sit side by side and the coalition wraps 3 + 5,
 * which is the 2026-09-02 layout and the client's own strip. S1 changed what is in the cells,
 * not where the cells are.
 *
 * **Each mark is a link.** Every logo opens its organisation's site in a new tab. The eight
 * coalition addresses and the donor's were each checked on 2026-09-08; two of them —
 * `iwmi.cgiar.org` and `cifor-icraf.org` — redirect, and `gateAssets.ts` links the destination
 * rather than making a reader take the hop.
 *
 * **The copy is the client's, verbatim.** Title, one-line subtitle, two paragraphs, five uses
 * and a bold tagline, in `locales/{en,fr}.json` under `gate.*`. It is longer than the two
 * paragraphs it replaces, which is why the panel's text is left-aligned now: a centred bullet
 * list is not a list.
 *
 * **The photograph stayed — until the same day's second round.** An earlier specification
 * wanted a live map behind this page and the client superseded that in S1; that evening they
 * reversed it, and the backdrop is now `GateMap` (see below). Translucency unchanged.
 *
 * ## 2026-09-08 (evening): the band moves right, and the panel behind the copy is a map
 *
 * Two client instructions, both in `docs/client-specs/2026-09-08-entry-s1.md`.
 *
 * **The attribution band is the right panel's last child.** *"Move the whole logo section to
 * the right, below, as it appears on the left — do not change anything else."* Same markup,
 * same order, same ids, same 3 + 5; the parent changed and nothing else did. `#gateForm` is a
 * flex column now — the form takes the space above and centres in it, the band is pinned at the
 * foot — and the column's rhythm is compressed (132px logo, 24px *Welcome*, four tighter
 * margins) so the two blocks clear a 900px-tall window without the panel scrolling.
 *
 * **The left half is a live map of the four programme countries** (`GateMap`): the default
 * basemap plus the level-0 outlines from `/hierarchy/units`, fitted to the programme extent,
 * decorative and completely inert. The film over it and the copy over that are unchanged, word
 * for word, in both languages. `backdrop.jpg` is untouched in the repository and
 * `gateAssets.BACKDROP` still resolves it; this page simply stops asking for it.
 *
 * ## The fields are inert, and that is the specification, not a stub
 *
 * The review asks for an email and a password that are *visible* and cost nothing: "no
 * validation, no credentials required, permissions come later". So they are real, focusable,
 * typeable inputs whose values are never read, never sent and never stored — uncontrolled,
 * outside React state entirely, with `autoComplete="off"` so no browser offers to save a
 * credential this app has no account to check it against. Two rejected alternatives, both
 * worse:
 *
 * * **`disabled` fields.** They would *look* like the gate is broken or still loading, and
 *   a reader who cannot click into an email box reads it as a fault to report.
 * * **A fake check.** Any validation at all — a required marker, a "wrong password"
 *   message, a spinner — would be a claim about an authentication system that does not
 *   exist yet. §I2's real one arrives with roles; until then the honest UI is one that lets
 *   anyone in and says so, which is what the hint under the buttons does.
 *
 * *Remember me* and *Forgot password?* join them: the mock has both, and both are inert for
 * exactly the same reason — there is no session to remember and no address to send a reset
 * to. They are marked `aria-disabled` rather than `disabled` so a screen reader hears why
 * instead of hearing nothing, and the hint under the form says it in words.
 *
 * ## "Continue as guest" stays, and the mock omits it
 *
 * The client's mock has *Sign In* and nothing else. Taken literally that would leave the only
 * way into a portal whose data is entirely open behind a button that authenticates nobody —
 * and would make the honest action (browsing as a guest) the one with no control. So the door
 * survives. Logged as a deviation in docs/NOTES.md.
 *
 * C5 demoted it to a discreet underlined line, and the 2026-09-02 review put it back to a
 * full-width button: until §I2 ships there is no account to sign in with, so the line of
 * 12 px text was the control that actually worked and the solid blue button was the one that
 * did nothing. It stays visually second — soft green fill, outlined, against Sign In's solid
 * blue — which is the mock's hierarchy without a 12 px tap target at the bottom of a phone.
 *
 * Both exits therefore still do exactly one thing, and the same thing: `setGateOpen(false)`.
 *
 * **ESC does not close it.** Deliberate, and the one place this overlay departs from the
 * splash's keyboard contract: the splash is a thing you dismiss, the gate is a thing you
 * pass through, and there is no hidden shortcut past it. Every exit is a labelled control.
 *
 * **It is a real modal, not just a `role` (R6 fix).** `aria-modal="true"` is a claim about
 * the whole document — it tells assistive tech to ignore everything outside this node — and a
 * dialog that makes that claim without moving focus into itself leaves a screen-reader user's
 * first screen of the session showing one dialog with their cursor nowhere in it. So on open:
 * focus lands on the email field, the rest of the document is marked `inert` (which also
 * takes the covered topbar and map controls out of the Tab order a sighted keyboard user was
 * walking through), and Tab cycles inside the page. On close, focus goes back where it came
 * from. §F2's `#toast` is deliberately left out of the `inert` sweep: it is the app's one
 * `aria-live` region, and a boot-time message (`PermalinkSync`'s "some shared settings were
 * not recognised") must still be announced to a reader who is standing at the gate.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { asLanguage, DEFAULT_LANGUAGE, otherLanguage, setLanguage } from '../../lib/i18n';
import { useAppStore } from '../../lib/state';
import { GateMap } from './GateMap';
import { LOGO, NETHERLANDS, NETHERLANDS_HREF, PARTNERS } from './gateAssets';

/**
 * The five things the client's copy says the Geoportal is for, in the order it lists them.
 * Keys rather than strings so the list is translated with everything else, and a constant
 * rather than five hand-written `<li>`s so that adding a sixth is one line in two JSON files.
 */
const USES = ['gate.use1', 'gate.use2', 'gate.use3', 'gate.use4', 'gate.use5'] as const;

/**
 * Everything inside the page a Tab can reach, in document order.
 *
 * Queried on each keypress rather than cached: the contents change under the reader (the
 * EN/FR button re-renders the whole form, the reveal toggle swaps its own icon), and a stale
 * list would trap Tab on a node that is no longer there.
 */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SignInGate() {
  const { t, i18n } = useTranslation();
  const open = useAppStore((s) => s.gateOpen);
  const setGateOpen = useAppStore((s) => s.setGateOpen);
  /**
   * Read off the i18next instance, as the topbar's toggle is, so it re-renders on
   * `languageChanged` whoever caused it.
   */
  const language = asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;

  /** The mock's eye toggle. The one piece of state on this page, and it is about pixels. */
  const [revealed, setRevealed] = useState(false);

  const enter = useCallback(() => setGateOpen(false), [setGateOpen]);

  /**
   * A real `<form>`, so Enter in either field submits — a sign-in page where the return key
   * does nothing is a sign-in page that feels broken. `preventDefault` because there is
   * nowhere to submit to; the handler is the same `enter` both exits call.
   */
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      enter();
    },
    [enter],
  );

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  /**
   * The modal half of `aria-modal`: focus in, the document behind out, focus back on close.
   *
   * The gate is a sibling of the whole app under `#root` (`app/App.tsx`), so "the document
   * behind" is precisely this node's siblings — no wrapper element, and therefore no chance of
   * a layout change from adding one.
   */
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!open || !overlay) return;

    const behind = Array.from(overlay.parentElement?.children ?? []).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== overlay && el.getAttribute('role') !== 'status',
    );
    for (const el of behind) el.setAttribute('inert', '');

    const cameFrom = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    emailRef.current?.focus();

    return () => {
      for (const el of behind) el.removeAttribute('inert');
      if (cameFrom?.isConnected) cameFrom.focus();
    };
  }, [open]);

  /**
   * Tab wraps inside the page. `inert` already keeps focus out of the app behind, but it does
   * not stop Tab leaving for the browser's own chrome and coming back somewhere arbitrary —
   * and it is the newer of the two mechanisms, so this is also the floor on a browser without.
   */
  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  if (!open) return null;

  return (
    <div
      id="gateOv"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gateTitle"
      data-testid="signin-gate"
      onKeyDown={trapTab}
    >
      <div id="gateCard">
        {/* ---------------------------------------------------------------- left: the panel */}
        <section id="gateArt">
          {/*
            The backdrop and its film are two absolutely-positioned layers rather than one
            element with a gradient stacked on it, so the dark wash is a fixed quantity the
            text contrast was checked against — and so the CSS gradient underneath is a real
            backdrop rather than a colour showing through a hole.

            **The backdrop is the programme's own map since 2026-09-08**, at the client's
            instruction: the four countries drawn live from `/hierarchy/units` over the
            deployment's basemap, decorative and inert (`GateMap`). The photograph's assets are
            untouched in the repository — `gateAssets.BACKDROP` still resolves `backdrop.jpg`
            for anything that wants it — and this page simply stops asking for one. The film
            and the copy over it are unchanged, which is the rest of the instruction: *"the
            SAME translucent dark film the photo had, and the SAME text content."*
          */}
          <GateMap />
          <div className="gate-art-wash" aria-hidden="true" />

          <div className="gate-art-inner">
            {/*
              The client's own copy, S1 priority 2, word for word: the name, the one-line
              promise under it, two paragraphs of what the portal holds, the five things it is
              for, and the tagline. `<strong>` on the last line rather than a CSS weight,
              because "bold" there is the client's emphasis and not this page's typography —
              it should survive a restyle and reach a screen reader.
            */}
            <div className="gate-art-mid">
              <h1 id="gateTitle">{t('gate.title')}</h1>
              <p className="gate-subtitle">{t('gate.subtitle')}</p>
              <p className="gate-lede">{t('gate.lede1')}</p>
              <p className="gate-lede">{t('gate.lede2')}</p>
              <p className="gate-uses-intro">{t('gate.usesIntro')}</p>
              <ul className="gate-uses">
                {USES.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
              <p className="gate-tagline">
                <strong>{t('gate.tagline')}</strong>
              </p>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- right: the form */}
        <section id="gateForm">
          {/* §F12's EN/FR toggle lives in the topbar — which is now *behind* the first page a
              reader sees. `initialLanguage()` reads a stored preference and otherwise falls
              back to English, so without this a francophone visitor's first screen would be
              English with no control on it. Same behaviour and same storage as the topbar's
              button; the label is the language you are *in*. */}
          <button
            type="button"
            id="gateLang"
            lang={language}
            title={t('topbar.switchLanguage')}
            aria-label={t('topbar.switchLanguage')}
            onClick={() => {
              void setLanguage(otherLanguage(language));
            }}
          >
            {t('topbar.lang')}
          </button>

          <div id="gateFormInner">
            <img className="gate-logo" src={LOGO} alt="" width={132} height={132} />
            <h2 className="gate-welcome">{t('gate.welcome')}</h2>
            <p className="gate-sub">{t('gate.sub')}</p>

            <form onSubmit={onSubmit} noValidate>
              <label className="fld" htmlFor="gateUser">
                {t('gate.email')}
              </label>
              <input
                id="gateUser"
                ref={emailRef}
                className="std"
                type="email"
                name="email"
                autoComplete="off"
                spellCheck={false}
                placeholder={t('gate.emailPh')}
              />

              <label className="fld" htmlFor="gatePass">
                {t('gate.password')}
              </label>
              <div className="gate-pass">
                <input
                  id="gatePass"
                  className="std"
                  type={revealed ? 'text' : 'password'}
                  name="password"
                  autoComplete="off"
                  placeholder={t('gate.passwordPh')}
                />
                <button
                  type="button"
                  id="gatePassEye"
                  className="gate-eye"
                  aria-controls="gatePass"
                  aria-pressed={revealed}
                  title={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  aria-label={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  onClick={() => setRevealed((v) => !v)}
                >
                  <Icon name={revealed ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>

              <div className="gate-row">
                {/*
                  `aria-disabled`, not `disabled`: both controls are in the mock and neither
                  does anything yet, and a control the browser refuses to focus cannot tell a
                  screen-reader user *why*. They are reachable, they announce themselves as
                  unavailable, and the handlers below refuse the interaction rather than
                  pretending to have performed it.
                */}
                <label className="gate-remember" htmlFor="gateRemember">
                  <input
                    type="checkbox"
                    id="gateRemember"
                    aria-disabled="true"
                    title={t('gate.inertControl')}
                    checked={false}
                    onChange={() => undefined}
                  />
                  {t('gate.remember')}
                </label>
                <button
                  type="button"
                  id="gateForgot"
                  className="gate-forgot"
                  aria-disabled="true"
                  title={t('gate.inertControl')}
                  onClick={(e) => e.preventDefault()}
                >
                  {t('gate.forgot')}
                </button>
              </div>

              <button type="submit" className="gate-signin" id="gateSignIn">
                {t('gate.signIn')}
              </button>
            </form>

            {/* The mock has no guest door. The portal's data is open and its honest action is
                to browse it, so the door stays — and after the 2026-09-02 review it is a
                full-width button again rather than a line of underlined text. Nobody signing
                in has an account yet, so on today's build *this* is the control that works,
                and a control that works has to be tappable on a phone. It is still second:
                soft-filled and outlined against Sign In's solid blue. */}
            <button type="button" className="gate-guest" id="gateGuest" onClick={enter}>
              {t('gate.guest')}
            </button>

            <p className="gate-hint">{t('gate.hint')}</p>
          </div>

          {/*
            The attribution band, arranged as IFDC's brand guide arranges it: one light card
            carrying *Funded by* with the donor mark on the left and *Coalition Partners*
            with the partner grid on the right, and a single dashed rule running the whole
            width under both headings.

            The rule is why this is one three-row grid rather than two columns side by side.
            A border under each heading would be two rules with the gutter's gap punched
            between them; here the headings occupy row 1, the rule is one element spanning
            row 2 from the first column to the last, and the artwork sits in row 3. Every
            child is placed explicitly (see base.css), so the document order below can stay
            the order a screen reader wants — each heading, then the marks it labels, tied
            together by `aria-labelledby` since the grid has separated them visually.

            **S1 corrections, 2026-09-08.** Two client rejections, and both of them restored
            something this round should not have touched. The first S1 build stacked the two
            groups to buy the coalition more width, which put the donor mark at the top of the
            band; the second wrapped the coalition three across in three rows. The band that
            ships is the 2026-09-02 one exactly: two columns, and the guide's 3 + 5 in the
            coalition — IFDC, SNV and Wageningen across the top, the other five beneath.

            What S1 built *inside* it all stays — trimmed files, one fixed cell size per row,
            the eye-balance, equal gaps, both rows on one centre line, the white chips, and a
            link on every mark including the donor's. The width the two rejected layouts were
            buying comes instead from numbers this round owns: the cell gap, the band's
            padding and the gutter between the two groups (base.css).

            Every mark is a link to its organisation (`gateAssets.ts` holds the addresses,
            each verified 2026-09-08). `rel="noopener noreferrer"` on all of them: these are
            third-party sites and none of them needs a handle on this window.

            The band is a light card for the reason the two white cards it replaced were:
            several of these logos are gradients or dark type on transparency (SNV, Landscape
            Alliance, IITA); dropped onto a dark ground they would be invisible, and inverting
            them is not ours to do to somebody's brand. Each mark additionally sits on its own
            white chip, which is what makes the "even grid" visible as a grid — the cells are
            identical, and now they look it. On the panel's own white it keeps the chips and
            takes a hairline border, because a white card on white needs an edge to be a card.

            **It is at the foot of the *right* panel since 2026-09-08**, at the client's
            instruction: *"move the whole logo section to the right, below, as it appears on
            the left — do not change anything else."* So this is the same markup it was, in the
            same order, with the same ids and the same 3 + 5; only its parent changed, from
            `.gate-art-inner` to `#gateForm`. The right panel's rhythm was compressed to make
            room for it (base.css) and nothing else on the page moved.
          */}
          <div className="gate-band" data-gateband="">
            <h2 className="gate-band-title gate-band-funded" id="gateFundedTitle">
              {t('gate.fundedBy')}
            </h2>
            <h2 className="gate-band-title gate-band-coalition" id="gatePartnersTitle">
              {t('gate.partners')}
            </h2>
            <div className="gate-band-rule" data-gatebandrule="" aria-hidden="true" />

            <div
              className="gate-nl"
              data-gatenl=""
              role="group"
              aria-labelledby="gateFundedTitle"
            >
              <a
                className="gate-mark gate-mark-donor"
                data-partner="netherlands"
                data-fit="wide"
                href={NETHERLANDS_HREF}
                target="_blank"
                rel="noopener noreferrer"
              >
                {NETHERLANDS ? (
                  <img src={NETHERLANDS} alt={t('gate.netherlands')} />
                ) : (
                  <span className="gate-mark-text">{t('gate.netherlands')}</span>
                )}
              </a>
            </div>

            <div
              className="gate-partner-row"
              data-gatepartners=""
              role="group"
              aria-labelledby="gatePartnersTitle"
            >
              {PARTNERS.map((p) => (
                <a
                  key={p.slug}
                  className="gate-mark gate-partner"
                  data-partner={p.slug}
                  data-fit={p.fit}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.url ? (
                    <img src={p.url} alt={p.name} />
                  ) : (
                    <span className="gate-mark-text gate-partner-text">{p.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SignInGate;
